import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../store';
import {
    fetchPlaces,
    fetchSinglePlace,
    addPlace,
    updatePlace,
    deletePlace,
    updatePlaceOrder,
    DeletePlace
} from './placeAsyncThunk';
import { Place } from '../../../types';
import { toast } from 'react-toastify';

interface PlaceState {
    data: Place[];
    selectedPlace: Place | null;
    status: 'idle' | 'loading' | 'succeeded' | 'failed';
    loading: boolean;
    error: string | null;
}

const initialState: PlaceState = {
    data: [],
    selectedPlace: null,
    status: 'idle',
    loading: false,
    error: null,
};

const placeSlice = createSlice({
    name: 'place',
    initialState,
    reducers: {
        setSelectedPlace: (state, action: PayloadAction<Place | null>) => {
            state.selectedPlace = action.payload;
        },
        clearPlaces: (state) => {
            state.data = [];
            state.selectedPlace = null;
        },
    },
    extraReducers: (builder) => {
        // Fetch all
        builder
            .addCase(fetchPlaces.pending, (state) => {
                state.status = 'loading';
                state.error = null;
            })
            .addCase(fetchPlaces.fulfilled, (state, action: PayloadAction<Place[]>) => {
                state.status = 'succeeded';
                state.data = action.payload;
            })
            .addCase(fetchPlaces.rejected, (state, action) => {
                state.status = 'failed';
                toast.error(action.payload || 'Failed to fetch places');
                state.error = action.payload || 'Failed to fetch places';
            });

        // Fetch single
        builder
            .addCase(fetchSinglePlace.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchSinglePlace.fulfilled, (state, action: PayloadAction<Place>) => {
                state.loading = false;
                state.selectedPlace = action.payload;
            })
            .addCase(fetchSinglePlace.rejected, (state, action) => {
                state.loading = false;
                toast.error(action.payload || 'Failed to fetch place');
                state.error = action.payload || 'Failed to fetch place';
            });

        // Add
        builder
            .addCase(addPlace.pending, (state) => {
                state.loading = true;
            })
            .addCase(addPlace.fulfilled, (state, action: PayloadAction<Place>) => {
                state.loading = false;
                state.data.push(action.payload);
                toast.success('Place added successfully');
            })
            .addCase(addPlace.rejected, (state, action) => {
                state.loading = false;
                toast.error(action.payload || 'Failed to add place');
                state.error = action.payload || 'Failed to add place';
            });

        // Update
        builder
            .addCase(updatePlace.pending, (state) => {
                state.loading = true;
            })
            .addCase(updatePlace.fulfilled, (state, action: PayloadAction<Place>) => {
                const index = state.data.findIndex((item) => item.id === action.payload.id);
                if (index !== -1) {
                    state.data[index] = action.payload;
                }
                if (state.selectedPlace?.id === action.payload.id) {
                    state.selectedPlace = action.payload;
                }
                state.loading = false;
                toast.success('Place updated successfully');
            })
            .addCase(updatePlace.rejected, (state, action) => {
                state.loading = false;
                toast.error(action.payload || 'Failed to update place');
                state.error = action.payload || 'Failed to update place';
            });

        // Delete
        builder
            .addCase(deletePlace.pending, (state) => {
                state.loading = true;
            })
            .addCase(deletePlace.fulfilled, (state, action: PayloadAction<DeletePlace>) => {
                state.data = state.data.filter((item) => item.id !== action.payload.id);
                if (state.selectedPlace?.id === action.payload.id) {
                    state.selectedPlace = null;
                }
                state.loading = false;
                toast.success('Place deleted successfully');
            })
            .addCase(deletePlace.rejected, (state, action) => {
                state.loading = false;
                toast.error(action.payload || 'Failed to delete place');
                state.error = action.payload || 'Failed to delete place';
            });

        // Update order
        builder
            .addCase(updatePlaceOrder.pending, (state) => {
                state.status = 'loading';
                state.loading = true;
            })
            .addCase(updatePlaceOrder.fulfilled, (state, action: PayloadAction<Place[]>) => {
                state.data = action.payload;
                state.status = 'succeeded';
                state.loading = false;
                toast.success('Order updated successfully');
            })
            .addCase(updatePlaceOrder.rejected, (state, action) => {
                state.status = 'failed';
                state.loading = false;
                toast.error(action.payload || 'Failed to update order');
                state.error = action.payload || 'Failed to update order';
            });
    },
});

export const { setSelectedPlace, clearPlaces } = placeSlice.actions;

export const selectPlace = (state: RootState) => state.place;

export default placeSlice.reducer;
